/**
 * Utility functions for converting description markup to HTML
 * Frontend version for preview purposes - the actual conversion happens on the backend
 */

export interface DescriptionConversionResult {
  originalText: string;
  htmlContent: string;
  hasMarkup: boolean;
  formatTypes: string[];
}

/**
 * Analyze description text to determine if it contains markup that will be converted
 * This is used for preview purposes in the import UI
 */
export function analyzeDescriptionMarkup(description: string): DescriptionConversionResult {
  if (!description || typeof description !== 'string') {
    return {
      originalText: '',
      htmlContent: '',
      hasMarkup: false,
      formatTypes: []
    };
  }

  const formatTypes: string[] = [];
  let hasMarkup = false;

  // Check for various Jira markup patterns
  const patterns = [
    { regex: /\*([^*\n]+)\*/, type: 'Bold' },
    { regex: /_([^_\n]+)_/, type: 'Italic' },
    { regex: /-([^-\n]+)-/, type: 'Strikethrough' },
    { regex: /\+([^+\n]+)\+/, type: 'Underline' },
    { regex: /\{\{([^}]+)\}\}/, type: 'Code' },
    { regex: /^h[1-6]\.\s/, type: 'Headings' },
    { regex: /^\*+\s/, type: 'Bullet Lists' },
    { regex: /^#+\s/, type: 'Numbered Lists' },
    { regex: /\[([^\]]+)\]/, type: 'Links' },
    { regex: /\{quote\}/, type: 'Quotes' },
    { regex: /\{code/, type: 'Code Blocks' }
  ];

  patterns.forEach(({ regex, type }) => {
    if (regex.test(description)) {
      hasMarkup = true;
      if (!formatTypes.includes(type)) {
        formatTypes.push(type);
      }
    }
  });

  // Check if it's already HTML
  const isHtml = description.includes('<') && description.includes('>');
  if (isHtml) {
    formatTypes.push('HTML');
    hasMarkup = true;
  }

  // Basic conversion preview (simplified version)
  let previewHtml = description;
  if (hasMarkup && !isHtml) {
    previewHtml = convertBasicMarkupPreview(description);
  } else if (isHtml) {
    previewHtml = description; // Already HTML
  } else {
    // Plain text - will be wrapped in paragraphs
    previewHtml = `<p>${description.replace(/\n/g, '<br>')}</p>`;
  }

  return {
    originalText: description,
    htmlContent: previewHtml,
    hasMarkup,
    formatTypes
  };
}

/**
 * Basic markup conversion for preview purposes
 * The actual conversion happens on the backend with more robust handling
 */
function convertBasicMarkupPreview(text: string): string {
  let html = text;

  // Basic conversions for preview
  html = html.replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>');
  html = html.replace(/_([^_\n]+)_/g, '<em>$1</em>');
  html = html.replace(/-([^-\n]+)-/g, '<del>$1</del>');
  html = html.replace(/\+([^+\n]+)\+/g, '<u>$1</u>');
  html = html.replace(/\{\{([^}]+)\}\}/g, '<code>$1</code>');

  // Basic heading conversion
  html = html.replace(/^h([1-6])\.\s*(.+)$/gm, '<h$1>$2</h$1>');

  // Basic link conversion
  html = html.replace(/\[([^\|\]]+)\|([^\]]+)\]/g, '<a href="$2">$1</a>');
  html = html.replace(/\[([^\]]+)\]/g, '<a href="$1">$1</a>');

  // Wrap in paragraphs if no block elements
  if (!html.includes('<h') && !html.includes('<ul>') && !html.includes('<ol>')) {
    const paragraphs = html.split(/\n\s*\n/);
    if (paragraphs.length > 1) {
      html = paragraphs
        .filter(p => p.trim())
        .map(p => `<p>${p.trim().replace(/\n/g, '<br>')}</p>`)
        .join('');
    } else {
      html = `<p>${html.replace(/\n/g, '<br>')}</p>`;
    }
  }

  return html;
}

/**
 * Get a plain text preview of the description (strip HTML tags)
 */
export function getPlainTextPreview(html: string, maxLength: number = 100): string {
  if (!html) return '';
  
  // Remove HTML tags
  const text = html.replace(/<[^>]*>/g, '');
  
  // Decode HTML entities
  const decoded = text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  
  // Truncate if needed
  if (decoded.length <= maxLength) return decoded;
  return decoded.substring(0, maxLength) + '...';
}

/**
 * Check if description contains rich content that benefits from HTML rendering
 */
export function hasRichContent(description: string): boolean {
  if (!description) return false;
  
  const richContentPatterns = [
    /<[^>]+>/, // HTML tags
    /\*[^*]+\*/, // Bold
    /_[^_]+_/, // Italic
    /\[[^\]]+\]/, // Links
    /^h[1-6]\./m, // Headings
    /^\*\s/m, // Bullet lists
    /^#\s/m, // Numbered lists
    /\{quote\}/, // Quotes
    /\{code/, // Code blocks
  ];
  
  return richContentPatterns.some(pattern => pattern.test(description));
}

export default {
  analyzeDescriptionMarkup,
  getPlainTextPreview,
  hasRichContent
};
